import { useQuery } from '@tanstack/react-query';

import {
	listOperatorsOptions,
	type OperatorListResponseView,
	type OperatorSummaryResponseView,
} from '@mopla/ticketshop-api';

import { IOperator } from '../types';

interface IUseOperatorsResult {
	operators: IOperator[];
	isLoading: boolean;
	error: Error | null;
}

/**
 * Map BE DTO to internal app model.
 * Logo, hero banner and wordmark come from BE-provided URLs; the card falls back
 * to a gradient placeholder when the banner is absent. BE fields are optional in
 * the generated types, so we default the required app fields defensively.
 */
const mapOperatorDto = (dto: OperatorSummaryResponseView): IOperator => ({
	id: dto.id ?? '',
	name: dto.name ?? '',
	serviceAreaDescriptor: dto.serviceAreaDescriptor ?? '',
	iconUrl: dto.iconUrl,
	heroUrl: dto.heroUrl,
	wordmarkUrl: dto.wordmarkUrl,
});

/**
 * Step 0 — Operator overview. GET /api/v1/operators
 * BE returns only operators that have ≥1 active tariff and ≥1 active ticket type.
 */
export const useOperators = (): IUseOperatorsResult => {
	const { data, isLoading, error } = useQuery({
		...listOperatorsOptions(),
		select: (res: OperatorListResponseView): IOperator[] =>
			(res.operators ?? []).map(mapOperatorDto),
	});

	return { operators: data ?? [], isLoading, error };
};
